/**
 * 草稿管理服务
 * 提供标签草稿的本地存储、恢复和同步功能
 */

import { setItem, getItem, removeItem, getAllKeys } from '../utils/storage';

// 草稿存储配置
const DRAFT_CONFIG = {
  KEY_PREFIX: 'label_draft_',          // 草稿key前缀
  MAX_DRAFTS: 10,                       // 最大草稿数量
  EXPIRE_DAYS: 7,                       // 草稿过期天数
  AUTO_SAVE_INTERVAL: 30000,            // 自动保存间隔(毫秒) - 30秒
  DEBOUNCE_DELAY: 2000,                 // 防抖延迟(毫秒) - 2秒
};

/**
 * 生成草稿ID
 * @param {string|number} labelId - 标签ID（编辑模式）
 * @param {string} templateVariant - 模板变体（创建模式）
 * @returns {string} 草稿ID
 */
export const generateDraftId = (labelId = null, templateVariant = 'default') => {
  if (labelId) {
    return `edit_${labelId}`;
  }
  // 创建模式：使用时间戳+模板变体生成唯一ID
  return `new_${templateVariant}_${Date.now()}`;
};

/**
 * 获取草稿存储key
 */
const getDraftKey = (draftId) => {
  return `${DRAFT_CONFIG.KEY_PREFIX}${draftId}`;
};

/**
 * 保存草稿
 * @param {string} draftId - 草稿ID
 * @param {Object} draftData - 草稿数据
 * @returns {Object} 保存结果 {success, message, savedAt}
 */
export const saveDraft = (draftId, draftData) => {
  try {
    const key = getDraftKey(draftId);
    const savedAt = new Date().toISOString();

    const draft = {
      id: draftId,
      savedAt,
      version: (draftData._version || 0) + 1,
      data: {
        labelName: draftData.labelName || '',
        backgroundImage: draftData.backgroundImage || null,
        backgroundColor: draftData.backgroundColor || '#ffffff',
        borderColor: draftData.borderColor || '#000000',
        templateVariant: draftData.templateVariant || 'default',
        labelMeta: draftData.labelMeta || {},
        contentItems: draftData.contentItems || [],
      },
      // 元信息
      meta: {
        isEditMode: draftData.isEditMode || false,
        editingLabelId: draftData.editingLabelId || null,
        initialTemplates: draftData.initialTemplates || [],
        createdAt: draftData._createdAt || savedAt,
      }
    };

    // 保存草稿，过期时间为配置的天数
    const expireMinutes = DRAFT_CONFIG.EXPIRE_DAYS * 24 * 60;
    const success = setItem(key, draft, expireMinutes);

    if (success) {
      // 清理过多的草稿
      cleanupOldDrafts();

      return {
        success: true,
        message: '草稿已保存',
        savedAt,
        version: draft.version,
      };
    }

    return {
      success: false,
      message: '草稿保存失败',
      savedAt: null,
    };
  } catch (error) {
    console.error('保存草稿失败:', error);
    return {
      success: false,
      message: error.message || '保存草稿时发生错误',
      savedAt: null,
    };
  }
};

/**
 * 获取草稿
 * @param {string} draftId - 草稿ID
 * @returns {Object|null} 草稿数据
 */
export const getDraft = (draftId) => {
  try {
    const key = getDraftKey(draftId);
    return getItem(key, null);
  } catch (error) {
    console.error('获取草稿失败:', error);
    return null;
  }
};

/**
 * 删除草稿
 * @param {string} draftId - 草稿ID
 * @returns {boolean} 是否删除成功
 */
export const deleteDraft = (draftId) => {
  try {
    const key = getDraftKey(draftId);
    return removeItem(key);
  } catch (error) {
    console.error('删除草稿失败:', error);
    return false;
  }
};

/**
 * 获取所有草稿列表
 * @returns {Array} 草稿列表
 */
export const getAllDrafts = () => {
  try {
    const allKeys = getAllKeys();
    const draftKeys = allKeys.filter(key => key.startsWith(DRAFT_CONFIG.KEY_PREFIX));

    const drafts = draftKeys
      .map(key => {
        const draftId = key.replace(DRAFT_CONFIG.KEY_PREFIX, '');
        return getDraft(draftId);
      })
      .filter(Boolean)
      .sort((a, b) => new Date(b.savedAt) - new Date(a.savedAt));

    return drafts;
  } catch (error) {
    console.error('获取草稿列表失败:', error);
    return [];
  }
};

/**
 * 查找特定标签的草稿（编辑模式）
 * @param {string|number} labelId - 标签ID
 * @returns {Object|null} 草稿数据
 */
export const findDraftByLabelId = (labelId) => {
  if (!labelId) return null;
  const draftId = `edit_${labelId}`;
  return getDraft(draftId);
};

/**
 * 查找最近的创建模式草稿
 * @param {string} templateVariant - 模板变体（可选）
 * @returns {Object|null} 最近的草稿
 */
export const findRecentCreateDraft = (templateVariant = null) => {
  const allDrafts = getAllDrafts();

  // 过滤出创建模式的草稿
  const createDrafts = allDrafts.filter(draft => {
    const isCreateMode = draft.id.startsWith('new_');
    if (!isCreateMode) return false;

    // 如果指定了模板变体，则只匹配该变体
    if (templateVariant) {
      return draft.data.templateVariant === templateVariant;
    }
    return true;
  });

  return createDrafts.length > 0 ? createDrafts[0] : null;
};

/**
 * 清理过多的草稿
 * 保留最新的 MAX_DRAFTS 个草稿
 */
export const cleanupOldDrafts = () => {
  try {
    const allDrafts = getAllDrafts();

    if (allDrafts.length > DRAFT_CONFIG.MAX_DRAFTS) {
      // 按时间排序，删除最旧的草稿
      const draftsToDelete = allDrafts.slice(DRAFT_CONFIG.MAX_DRAFTS);

      draftsToDelete.forEach(draft => {
        deleteDraft(draft.id);
      });

      console.log(`已清理 ${draftsToDelete.length} 个过期草稿`);
    }
  } catch (error) {
    console.error('清理草稿失败:', error);
  }
};

/**
 * 清空所有草稿
 * @returns {number} 删除的草稿数量
 */
export const clearAllDrafts = () => {
  try {
    const allDrafts = getAllDrafts();
    let count = 0;

    allDrafts.forEach(draft => {
      if (deleteDraft(draft.id)) {
        count++;
      }
    });

    return count;
  } catch (error) {
    console.error('清空草稿失败:', error);
    return 0;
  }
};

/**
 * 检查草稿是否有变更
 * @param {Object} currentData - 当前数据
 * @param {Object} savedDraft - 已保存的草稿
 * @returns {boolean} 是否有变更
 */
export const hasChanges = (currentData, savedDraft) => {
  if (!savedDraft) return true;

  try {
    const current = JSON.stringify({
      labelName: currentData.labelName,
      contentItems: currentData.contentItems,
      labelMeta: currentData.labelMeta,
      backgroundImage: currentData.backgroundImage,
      backgroundColor: currentData.backgroundColor,
    });

    const saved = JSON.stringify({
      labelName: savedDraft.data.labelName,
      contentItems: savedDraft.data.contentItems,
      labelMeta: savedDraft.data.labelMeta,
      backgroundImage: savedDraft.data.backgroundImage,
      backgroundColor: savedDraft.data.backgroundColor,
    });

    return current !== saved;
  } catch (error) {
    return true;
  }
};

/**
 * 格式化保存时间
 * @param {string} isoString - ISO时间字符串
 * @returns {string} 格式化的时间
 */
export const formatSavedTime = (isoString) => {
  if (!isoString) return '';

  try {
    const date = new Date(isoString);
    const now = new Date();
    const diff = now - date;

    // 1分钟内
    if (diff < 60 * 1000) {
      return '刚刚保存';
    }

    // 1小时内
    if (diff < 60 * 60 * 1000) {
      const minutes = Math.floor(diff / (60 * 1000));
      return `${minutes}分钟前保存`;
    }

    // 今天
    if (date.toDateString() === now.toDateString()) {
      return `今天 ${date.toLocaleTimeString('zh-CN', { hour: '2-digit', minute: '2-digit' })} 保存`;
    }

    // 昨天
    const yesterday = new Date(now);
    yesterday.setDate(yesterday.getDate() - 1);
    if (date.toDateString() === yesterday.toDateString()) {
      return `昨天 ${date.toLocaleTimeString('zh-CN', { hour: '2-digit', minute: '2-digit' })} 保存`;
    }

    // 其他日期
    return date.toLocaleString('zh-CN', {
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    }) + ' 保存';
  } catch (error) {
    return '';
  }
};

// 导出配置（用于自定义）
export const DRAFT_SETTINGS = DRAFT_CONFIG;

export default {
  generateDraftId,
  saveDraft,
  getDraft,
  deleteDraft,
  getAllDrafts,
  findDraftByLabelId,
  findRecentCreateDraft,
  cleanupOldDrafts,
  clearAllDrafts,
  hasChanges,
  formatSavedTime,
  DRAFT_SETTINGS,
};
